// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import './ChatBot.css';
import { getAccessToken as getAIMediaToken } from '../services/aiMediaApi';

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || 'https://annadaneshwar-ai.azurewebsites.net/api/chatbot';
// Chat now reuses the same Auth0 M2M token used for image generation to avoid duplicate secrets.
const CHAT_API_TOKEN = process.env.REACT_APP_CHAT_API_TOKEN;

const ChatBot = ({ className = 'fixed' }) => {
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "Hi! I'm your AI assistant. How can I help you today? You can ask me about navigation, features, or any questions about Servantica!", 
      sender: 'bot' 
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendToApi = async (userMessage) => {
    const headers = { 'Content-Type': 'application/json' };

    // Prefer the shared AI media token (Auth0 M2M) to keep one auth surface.
    try {
      const token = await getAIMediaToken();
      headers.Authorization = `Bearer ${token}`;
    } catch (tokenErr) {
      // Fallback to legacy env token if present.
      if (CHAT_API_TOKEN) {
        headers.Authorization = `Bearer ${CHAT_API_TOKEN}`;
      } else {
        console.warn('Chat token unavailable', tokenErr);
      }
    }

    const res = await fetch(CHAT_API_URL, {
      method: 'POST',
      headers,
      mode: 'cors',
      body: JSON.stringify({ message: userMessage }),
    });

    if (!res.ok) {
      throw new Error(`API error ${res.status}`);
    }

    const text = await res.text();
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === 'string') return parsed;
      if (parsed?.message) return parsed.message;
      if (parsed?.reply) return parsed.reply;
    } catch (e) {
      // non-JSON, fall through
    }
    return text || 'No response received.';
  };

  const pushMessage = (text, sender) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), text, sender }]);
  };

  const handleSend = async (userMessage) => {
    pushMessage(userMessage, 'user');
    setLoading(true);

    try {
      const botResponse = await sendToApi(userMessage);
      pushMessage(botResponse, 'bot');
      toast.success('Response received!');
    } catch (error) {
      console.error('Chat error:', error);
      pushMessage('Sorry, I could not reach the AI service right now.', 'bot');
      toast.error('Failed to get response');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    handleSend(userMessage);
  };

  const toggleMinimize = () => setMinimized(!minimized);

  if (minimized) {
    return (
      <div className={`chatbot-container ${className}`} style={{ height: 'auto' }}>
        <div className="chatbot-header">
          <div className="chatbot-header-left">
            <span className="status-dot" aria-hidden="true" />
            <div>
              <h3>AI Assistant</h3>
              <p className="header-subtitle">Always on and ready</p>
            </div>
          </div>
          <button onClick={toggleMinimize} className="close-button" aria-label="Expand chatbot">⌃</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`chatbot-container ${className}`}>
      <div className="chatbot-header">
        <div className="chatbot-header-left">
          <span className="status-dot" aria-hidden="true" />
          <div>
            <h3>AI Assistant</h3>
            <p className="header-subtitle">Live on Servantica</p>
          </div>
        </div>
        <button onClick={toggleMinimize} className="close-button" aria-label="Minimize chatbot">⌄</button>
      </div>
      <div className="chatbot-messages">
        {messages.map((msg) => (
          <div key={msg.id} className={`message ${msg.sender}`}>
            {msg.text}
          </div>
        ))}
        {loading && <div className="typing-indicator">AI is typing...</div>}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="chatbot-input">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          disabled={loading}
        />
        <button type="submit" disabled={loading || !input.trim()}>
          Send
        </button>
      </form>
    </div>
  );
};

ChatBot.propTypes = {
  className: PropTypes.string
};

export default ChatBot;
